import { ENTITY_META } from '@/config/entityMeta';
import { readAxis } from './fields';
import type { Entity, Link } from '@/types/entity';

/**
 * 명제의 모양 (S4) — 작품의 핵심 논증(분석·통제 ↔ 합일·수용)을 데이터에서 파생.
 * 정적 산문(바이블 철학 축)이 아니라 행성 axis 값에서 *집계*한 살아있는 분포 + 거울 대립쌍(척추).
 * derive-don't-declare: planet.axis가 바뀌면 무게중심·쏠림이 자동 재계산.
 */

export interface AxisPoint {
  id: string;
  name: string;
  axis: number; // 0(분석)~1(합일)
  color: string;
}
export type AxisLeaning = 'analysis' | 'union' | 'balanced';

export interface AxisModel {
  points: AxisPoint[]; // axis 있는 행성, axis 오름차순
  centroid: number | null; // 평균 axis(무게중심)
  leaning: AxisLeaning | null;
  missing: number; // axis 미정 행성 수(초대)
}

export function deriveAxisModel(planets: Entity[]): AxisModel {
  const points: AxisPoint[] = [];
  let missing = 0;
  for (const p of planets) {
    const axis = readAxis(p);
    if (axis === undefined) {
      missing++;
      continue;
    }
    points.push({ id: p.id, name: p.name, axis, color: p.color ?? ENTITY_META[p.type].color });
  }
  points.sort((a, b) => a.axis - b.axis || (a.id < b.id ? -1 : 1));
  const centroid = points.length ? points.reduce((s, p) => s + p.axis, 0) / points.length : null;
  // 부동소수 경계 오분류 방지(예: (0.1+0.7)/2=0.3999…가 analysis로 새는 것).
  const EPS = 1e-9;
  const leaning: AxisLeaning | null =
    centroid === null ? null : centroid < 0.4 - EPS ? 'analysis' : centroid > 0.6 + EPS ? 'union' : 'balanced';
  return { points, centroid, leaning, missing };
}

/** 거울 대립쌍 — 명제의 척추(foil). mirror 링크에서 파생(양쪽 active). */
export interface MirrorPair {
  id: string;
  a: Entity;
  b: Entity;
  label?: string;
}

export function deriveMirrorPairs(links: Link[], emap: Record<string, Entity>): MirrorPair[] {
  const out: MirrorPair[] = [];
  const seen = new Set<string>(); // 무방향 쌍 정규화(중복·역방향 1회만)
  for (const l of links) {
    if (l.type !== 'mirror' || l.from === l.to) continue; // self-loop 제외
    const a = emap[l.from];
    const b = emap[l.to];
    if (!a || !b || a.deletedAt || b.deletedAt) continue;
    const key = [l.from, l.to].sort().join(' ');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id: l.id, a, b, label: l.label });
  }
  return out;
}
